import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "../components/Reveal";
import { company } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { value: "general", label: "General Enquiry" },
  { value: "security", label: "Security" },
  { value: "housekeeping", label: "Housekeeping" },
  { value: "events", label: "Event Management" },
  { value: "valet", label: "Valet Parking" },
];

const hubs = [
  { city: "Hyderabad", label: "Headquarters — Narsingi", phone: "+91 7416064672" },
];

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all duration-200";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "general", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.email.trim()) errs.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.phone.trim()) errs.phone = "Please enter your phone number";
    else if (!/^[+]?[\d\s-]{7,16}$/.test(form.phone.trim())) errs.phone = "Please enter a valid phone number";
    if (!form.message.trim()) errs.message = "Please enter your message";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      toast.success("Enquiry sent — we will respond within one business day.");
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <Reveal>
            <Eyebrow>Contact Us</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-slate-900" data-testid="contact-headline">
              Let's talk about your facility.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <div className="space-y-6" data-testid="contact-info-block">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white border border-slate-200 text-sky-700 shrink-0">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Phone</p>
                    <p className="text-sm text-slate-600" data-testid="contact-phone">{company.phone} · {company.phone2}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white border border-slate-200 text-sky-700 shrink-0">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Email</p>
                    <p className="text-sm text-slate-600" data-testid="contact-email">{company.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white border border-slate-200 text-sky-700 shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Headquarters</p>
                    <p className="text-sm text-slate-600" data-testid="contact-address">{company.address}</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="contact-hours-card">
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">Working Hours</p>
                <p className="mt-3 text-sm font-semibold text-slate-900">24/7 — Security never sleeps, and neither do we.</p>
                <p className="mt-2 text-sm text-slate-600">Office enquiries: Mon–Sat, 9:00 AM – 6:00 PM</p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid sm:grid-cols-3 gap-4">
                {hubs.map((h, i) => (
                  <div key={h.city} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`hub-card-${i}`}>
                    <p className="text-sm font-semibold text-slate-900">{h.city}</p>
                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-sky-700 mt-0.5">{h.label}</p>
                    <p className="text-xs text-slate-500 mt-2">{h.phone}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {submitted ? (
                <div
                  className="h-full min-h-96 rounded-2xl border border-slate-200 bg-white p-12 flex flex-col items-center justify-center text-center"
                  data-testid="contact-success-message"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl sm:text-3xl tracking-tight text-slate-900">
                    Thank you, {form.name.split(" ")[0]}.
                  </h2>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                    Your enquiry has been received. A member of our team will respond within one
                    business day.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "general", message: "" }); }}
                    className="mt-8 rounded-full border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-700 hover:border-sky-600 hover:text-sky-700 transition-colors duration-200"
                    data-testid="contact-send-another-button"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  noValidate
                  className="rounded-2xl border border-slate-200 bg-white p-8 lg:p-10 shadow-[0_4px_24px_-4px_rgba(15,23,42,0.06)]"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-semibold text-slate-800 mb-2">
                        Name <span className="text-sky-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Cooper"
                        className={inputClass}
                        data-testid="contact-name-input"
                      />
                      {errors.name && <p className="mt-1.5 text-xs text-red-500" data-testid="contact-name-error">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-800 mb-2">
                        Email <span className="text-sky-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jane@company.com"
                        className={inputClass}
                        data-testid="contact-email-input"
                      />
                      {errors.email && <p className="mt-1.5 text-xs text-red-500" data-testid="contact-email-error">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-semibold text-slate-800 mb-2">
                        Phone <span className="text-sky-600">*</span>
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+91 98765 43210"
                        className={inputClass}
                        data-testid="contact-phone-input"
                      />
                      {errors.phone && <p className="mt-1.5 text-xs text-red-500" data-testid="contact-phone-error">{errors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-service" className="block text-sm font-semibold text-slate-800 mb-2">
                        Service of interest
                      </label>
                      <select
                        id="contact-service"
                        value={form.service}
                        onChange={set("service")}
                        className={inputClass}
                        data-testid="contact-service-select"
                      >
                        {serviceOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className="block text-sm font-semibold text-slate-800 mb-2">
                        Message <span className="text-sky-600">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        rows={6}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us about your property, event or requirement..."
                        className={`${inputClass} resize-none`}
                        data-testid="contact-message-input"
                      />
                      {errors.message && <p className="mt-1.5 text-xs text-red-500" data-testid="contact-message-error">{errors.message}</p>}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-8 py-3.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                  >
                    {submitting ? "Sending..." : "Send Enquiry"}
                    <Send className={`h-4 w-4 transition-transform duration-300 ${submitting ? "" : "group-hover:translate-x-1"}`} />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
